<template>
	<div class="digital-edu-course-form fill-height">
		<div v-if="_.isNil(model)" class="fill-height w-100 d-flex flex-column flex-nowrap justify-center align-center">
			<div class="text-h5">Выберите элемент в списке слева</div>
			<div v-if="allowEdit">или</div>
			<div v-if="allowEdit" class="text-h5">создайте новый ЦОК</div>
		</div>
		<div v-else>
			<h3 class="d-flex flex-row flex-nowrap justify-start align-center">
				<div v-if="!form.isNewRecord">
					<div>Редактирование элемента</div>
					<div>&laquo;{{ model.courseName }}&raquo;</div>
				</div>
				<span v-else>Создание нового элемента</span>
			</h3>

			<v-divider class="my-2" />

			<div class="form-group">
				<div class="d-flex flex-row flex-wrap justify-between align-center">
					<div v-if="form.isNewRecord">
						<v-select
							v-model="$v.form.version.$model"
							:error-messages="getErrors(`form.version`)"
							:items="eduCourseModelVersionList"
							:menu-props="{
								maxHeight: 480
							}"
							label="Вариант контента"
							persistent-hint
							persistent-placeholder
							:return-object="false"
						>
						</v-select>
					</div>
					<div v-else>
						<label class="v-label v-label--active v-label--dense theme--light">Вариант контента</label>
						<div v-html="versionFormatted"></div>
					</div>

					<v-spacer />
					<v-btn 
						dark 
						color="purple darken-3" 
						@click.prevent.stop="copyVersion"
					>
						<v-icon left>mdi-content-copy</v-icon>
						Копировать версию
					</v-btn>
				</div>
			</div>

			<v-divider />

			<!-- Sync -->
			<div v-if="allowEdit && isVersionPublic" class="form-group d-flex flex-row justify-space-between align-center">
				<div>	
					<v-switch
						v-model="form.sync"
						label="Разрешить выгрузку"
						:true-value="1"
						:false-value="0"
					></v-switch>
				</div>
				<div v-if="!(form.isNewRecord || !model.sync)" class="d-flex flex-column justify-start align-center">
					<v-btn 
						dark 
						color="purple darken-3" 
						:loading="syncLoading"
						@click.prevent.stop="sync"
					>
						<v-icon left>mdi-cloud-sync-outline</v-icon>
						<span>Выгрузить на сервер</span>
					</v-btn>
					<span class="text-overline">{{ model.synced ? 'Выгружено' : 'Не выгружено' }}</span>
				</div>
			</div>

			<v-divider />

			<!-- Upload course data -->
			<div class="form-group">
				<label class="v-label v-label--active v-label--dense theme--light">Загруженный контент</label>
				<div v-if="!form.isNewRecord" class="d-flex flex-row flex-wrap justify-between align-start">
					<div class="d-flex flex-column justify-start align-start">
						<div>
							<v-btn v-if="!_.isNil(model.url)" :href="model.url" target="_blank">
								<v-icon left>mdi-eye</v-icon>
								Предпросмотр
							</v-btn>
							<div v-else class="text--secondary">Пока нет загруженного контента</div>
						</div>

						<div v-if="allowEdit && !form.isNewRecord && !_.isNil(model.url)" class="mt-2">
							<v-btn 
								:loading="linksLoading"
								@click.prevent.stop="getContentLinks"
							>
								<v-icon left>mdi-link</v-icon>
								Ссылки на элементы контента [CSV]
							</v-btn>
						</div>

						<div v-if="allowEdit && !form.isNewRecord && !_.isNil(model.url)" class="mt-2">
							<v-btn 
								:loading="linksWordLoading"
								@click.prevent.stop="getContentLinksWord"
							>
								<v-icon left>mdi-link</v-icon>
								Ссылки на элементы контента [Word]
							</v-btn>
						</div>
					</div>
					<v-spacer />
					<v-btn 
						v-if="allowEdit && !form.isNewRecord && !_.isNil(model.url)"
						dark 
						color="purple darken-3" 
						:loading="downloadLoading"
						class="mr-2"
						@click.prevent.stop="downloadZip"
					>
						<v-icon left>mdi-upload</v-icon>
						Скачать контент ЦОК
					</v-btn>
					<v-btn 
						v-if="allowEdit && !form.isNewRecord"
						dark 
						color="purple darken-3" 
						@click.prevent.stop="uploadZip"
					>
						<v-icon left>mdi-upload</v-icon>
						Загрузить контент ЦОК в виде архива
					</v-btn>	
				</div>
				<div v-else class="text--secondary">Контент можно загрузить только после создания элемента.</div>
			</div>

			<v-divider />

			<!-- Merge -->
			<div v-if="!form.isNewRecord && model.isMerge" class="form-group">
				<label class="v-label v-label--active v-label--dense theme--light">Объединенный ЦОК</label>
				<div>
					<v-btn dark color="purple darken-3" @click.prevent.stop="mergeUpdate">
						<v-icon left>mdi-refresh</v-icon>
						Обновить составные части
					</v-btn>
				</div>
			</div>
			<!-- Enabled -->
			<div
				v-if="allowEdit"
				class="form-group d-flex flex-row"
			>
				<div>	
					<v-switch
						v-model="form.enabled"
						label="Отображение включено"
						:true-value="1"
						:false-value="0"
					></v-switch>
				</div>
			</div>
			<!-- Preview -->
			<div class="form-group" v-if="!form.isNewRecord">
				<label class="v-label v-label--active v-label--dense theme--light">Публичная ссылка</label>
				<div
					class="
						d-flex
						flex-row flex-nowrap
						justify-start
						align-stretch
					"
				>
					<v-text-field
						dense
						:value="model.versionUrl"
						outlined
						hide-details
						type="text"
						readonly
					/>
					<v-btn
						color="purple darken-3"
						:elevation="0"
						tile
						dark
						:href="model.versionUrl"
						target="_blank"
						class="text-append-btn h-auto"
					>
						<v-icon>mdi-open-in-new</v-icon>
					</v-btn>
				</div>
			</div>

            <!-- Is School book -->
            <div class="form-group d-flex">
                <v-switch
                    v-model="form.preview.isSchoolbook"
                    label="Электронный учебник"
                    :true-value="1"
                    :false-value="0"
                ></v-switch>

                <v-switch
                    v-if="form.preview.isSchoolbook"
                    v-model="form.preview.showSidebar"
                    label="Отображать оглавление"
                    :true-value="1"
                    :false-value="0"
                    class="ml-5"
                ></v-switch>
            </div>

			<div class="form-group" v-if="!_.isNil(model.url) && isVersionMinistry">
				<label class="v-label v-label--active v-label--dense theme--light">Фон главного экрана</label>
				<v-img 
					v-if="_.size(_.get(form, 'preview.rootBackgroundUrl', '')) > 0" 
					:src="form.preview.rootBackgroundUrl" 
					:max-width="250"
					:max-height="250"
				/>
				<label v-if="allowEdit" for="root-background-image-upload-input" class="d-flex flex-row flex-nowrap justify-start align-center cursor-pointer">
					<v-file-input
						:key="_.now()"
						hide-input
						show-size
						prepend-icon="mdi-image-plus"
						accept="image/*"
						class="flex-grow-0 pt-0 mt-0"
						id="root-background-image-upload-input"
						@change="uploadPreviewRootBackground($event)"
					></v-file-input>
					<span class="text-overline">Загрузить новое изображение</span>
				</label>
			</div>
			<!-- Apps -->
			<div v-if="form.courseTypeId === 0 && isVersionMinistry" class="form-group">
				<label class="v-label v-label--active v-label--dense theme--light">Приложения</label>
				<div>
					<v-simple-table dense>
						<thead>
							<tr>
								<th class="text-left">Приложение</th>
								<th class="text-left">Описание</th>
								<th v-if="false" class="text-center" style="width: 1px;">&nbsp;</th>
								<th class="text-center" style="width: 1px;">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Приложение для Windows</td>
								<td class="text--secondary">Для Windows 7+</td>
								<td v-if="false" class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												 icon 
												 :href="model.url" 
												 target="_blank" 
												 v-bind="attrs" 
												 v-on="on"
												 :class="{unavailable: form.isNewRecord || _.isNil(model.url)}"
												 @click.prevent.stop="buildApp('windows')"
											>
												<v-icon>mdi-puzzle-plus</v-icon>
											</v-btn>
										</template>
										<span>Собрать приложение</span>
									</v-tooltip>
								</td>
								<td class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn 
												icon 
												:href="model.appWindowsUrl" 
												target="_blank" 
												v-bind="attrs" 
												v-on="on"
												:class="{unavailable: form.isNewRecord || _.isNil(model.appWindowsUrl)}"
											>
												<v-icon>mdi-download</v-icon>
											</v-btn>
										</template>
										<span>Скачать</span>
									</v-tooltip>
								</td>
							</tr>
							<tr>
								<td>Приложение для Linux (deb)</td>
								<td class="text--secondary">Для Ubuntu и AstraLinux</td>
								<td v-if="false" class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												 icon 
												 target="_blank" 
												 v-bind="attrs" 
												 v-on="on"
												 :class="{unavailable: form.isNewRecord || _.isNil(model.url)}"
												 @click.prevent.stop="buildApp('linux')"
											>
												<v-icon>mdi-puzzle-plus</v-icon>
											</v-btn>
										</template>
										<span>Собрать приложение</span>
									</v-tooltip>
								</td>
								<td class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn 
												icon 
												:href="model.appLinuxUrl" 
												target="_blank" 
												v-bind="attrs" 
												v-on="on"
												:class="{unavailable: form.isNewRecord || _.isNil(model.appLinuxUrl)}"
											>
												<v-icon>mdi-download</v-icon>
											</v-btn>
										</template>
										<span>Скачать</span>
									</v-tooltip>
								</td>
							</tr>
							<tr>
								<td>Приложение для Linux (rpm)</td>
								<td class="text--secondary">Для Fedora, RedOS</td>
								<td v-if="false" class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												 icon 
												 target="_blank" 
												 v-bind="attrs" 
												 v-on="on"
												 :class="{unavailable: form.isNewRecord || _.isNil(model.url)}"
												 @click.prevent.stop="buildApp('linux_rpm')"
											>
												<v-icon>mdi-puzzle-plus</v-icon>
											</v-btn>
										</template>
										<span>Собрать приложение</span>
									</v-tooltip>
								</td>
								<td class="text-center">
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn 
												icon 
												:href="model.appLinuxRpmUrl" 
												target="_blank" 
												v-bind="attrs" 
												v-on="on"
												:class="{unavailable: form.isNewRecord || _.isNil(model.appLinuxRpmUrl)}"
											>
												<v-icon>mdi-download</v-icon>
											</v-btn>
										</template>
										<span>Скачать</span>
									</v-tooltip>
								</td>
							</tr>
						</tbody>
					</v-simple-table>
				</div>
			</div>
			<!-- Apps icon -->
			<div v-if="form.courseTypeId === 0 && isVersionMinistry" class="form-group">
				<label class="v-label v-label--active v-label--dense theme--light">Иконка приложения</label>
				<div>
					<div>
						<v-img 
							v-if="_.size(_.get(form, 'extra.appIcon', '')) > 0" 
							:src="form.extra.appIcon" 
							:max-width="256"
							:max-height="256"
						/>
						<label v-if="allowEdit" for="extra-app-icon-input" class="d-flex flex-row flex-nowrap justify-start align-center cursor-pointer">
							<v-file-input
								:key="_.now()"
								hide-input
								show-size
								prepend-icon="mdi-image-plus"
								accept="image/*"
								class="flex-grow-0 pt-0 mt-0"
								id="extra-app-icon-input"
								@change="uploadExtraAppIcon($event)"
							></v-file-input>
							<span class="text-overline">Загрузить иконку</span>
						</label>
					</div>
				</div>
			</div>
			<!-- PDF doc -->
			<div v-if="form.courseTypeId === 0" class="form-group">
				<label class="v-label v-label--active v-label--dense theme--light">Методическое пособие</label>
				<div>
					<div>
						<v-text-field 
							v-model="form.extra.methodicalManual"
							:error-messages="getErrors('form.extra.methodicalManual', {
								url: 'Неверный формат ссылки'
							})"
							dense
							readonly
							persistent-placeholder
							persistent-hint
						>
							<template v-slot:prepend>
								<label v-if="allowEdit" for="extra-app-methodical-manual" class="d-flex flex-row flex-nowrap justify-start align-center cursor-pointer">
									<v-file-input
										:key="_.now()"
										hide-input
										show-size
										prepend-icon="mdi-file-plus"
										accept="application/pdf"
										class="flex-grow-0 pt-0 mt-0"
										id="extra-app-methodical-manual"
										@change="uploadExtraMethodicalManual($event)"
									></v-file-input>
								</label>
							</template>
							<template v-slot:append-outer>
								<v-btn v-if="_.size(form.extra.methodicalManual) > 0" icon :href="form.extra.methodicalManual" target="_blank">
									<v-icon>mdi-link</v-icon>
								</v-btn>
							</template>
						</v-text-field>
					</div>
				</div>
			</div>

			<div :class="{'untouchable opacity-45': !allowEdit}">
				<!-- Course type -->
				<div class="form-group">
					<label class="v-label v-label--active v-label--dense theme--light">Тип учебного матeриала</label>
					<div>{{ typeLabel }}</div>
				</div>
				<!-- External parent id -->
				<div v-if="!_.isNil(eduCourseParentModel)" class="form-group">
					<label class="v-label v-label--active v-label--dense theme--light">Родительский элемент</label>
					<div class="text-truncate">{{ eduCourseParentModel.courseName }}</div>
				</div>
				<!-- External Id -->
				<div class="form-group">
					<div v-if="!form.isNewRecord">
						<label class="v-label v-label--active v-label--dense theme--light">Уникальный индентифкатор</label>
						<div>{{ form.externalId }}</div>
					</div>
					<div v-else>
						<v-text-field 
							v-model="$v.form.externalId.$model"
							:error-messages="getErrors('form.externalId', {
								format: 'Недопустимые символы'
							})"
							dense
							label="Уникальный индентифкатор"
							persistent-placeholder
							persistent-hint
							hint="Только латиница, цифры и символы: _ -"
						></v-text-field>
					</div>
				</div>
				<!-- Course name -->
				<div class="form-group">
					<v-text-field 
						v-model="$v.form.courseName.$model"
						:error-messages="getErrors('form.courseName')"
						dense
						label="Название"
						persistent-placeholder
					></v-text-field>
				</div>
				<!-- Course description -->
				<div class="form-group">
					<v-textarea
						v-model="$v.form.courseDescription.$model"
						:error-messages="getErrors('form.courseDescription')"
						dense
						label="Описание"
						clearable
						clear-icon="mdi-close-circle"
						persistent-placeholder
						persistent-hint
						no-resize
					></v-textarea>
				</div>
				<!-- Course image -->
				<div class="form-group">
					<label class="v-label v-label--active v-label--dense theme--light" :class="{'error--text': $v.form.courseImage.$error}">Изображение учебного материла</label>
					<v-img 
						v-if="_.size(form.courseImage) > 0" 
						:src="imageSrc" 
						:max-width="250"
						:max-height="250"
					/>
					<label for="course-image-upload-input" class="d-flex flex-row flex-nowrap justify-start align-center cursor-pointer">
						<v-file-input
							:key="_.now()"
							hide-input
							show-size
							prepend-icon="mdi-image-plus"
							accept="image/png"
							class="flex-grow-0 pt-0 mt-0"
							id="course-image-upload-input"
							@change="uploadImage($event)"
						></v-file-input>
						<span class="text-button">Загрузить новое изображение</span>
					</label>
					<div v-if="$v.form.courseImage.$error" class="v-text-field__details">
						<div class="v-messages theme--light error--text" role="alert">
							<div class="v-messages__wrapper">
								<div class="v-messages__message">Обязательно</div>
							</div>
						</div>
					</div>
				</div>
				<!-- External link -->
				<div class="form-group">
					<v-text-field 
						v-model="$v.form.externalLink.$model"
						:error-messages="getErrors('form.externalLink', {
							url: 'Неверный формат ссылки'
						})"
						dense
						readonly
						label="Ссылка в системе-источнике"
						persistent-placeholder
						persistent-hint
					>
						<template v-slot:append-outer>
							<v-btn v-if="!form.isNewRecord" icon :href="form.externalLink" target="_blank">
								<v-icon>mdi-link</v-icon>
							</v-btn>
						</template>
					</v-text-field>
				</div>
				<!-- Grades -->
				<div class="form-group">
					<v-select
						v-model="$v.form.grades.$model"
						:error-messages="getErrors('form.grades')"
						:items="grades"
						:menu-props="{
							maxHeight: 480
						}"
						label="Классы / курсы"
						multiple
						persistent-hint
						persistent-placeholder
						:return-object="false"
					>
						<template v-slot:prepend-item>
							<v-list-item
								ripple
								@mousedown.prevent
								@click="toggleGradesSelectAll"
							>
								<v-list-item-action>
									<v-icon :color="form.grades.length > 0 ? 'indigo darken-4' : ''">
										{{ gradesSelectAllIcon }}
									</v-icon>
								</v-list-item-action>
								<v-list-item-content>
									<v-list-item-title>
										Выбрать все
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
							<v-divider class="mt-2"></v-divider>
						</template>
						<template v-slot:selection="{ item, index }">
							<span>{{ index > 0 ? ', ' : '' }}{{ getShortGrade(item.text) }}</span>
						</template>
					</v-select>
				</div>
				<!-- Tags -->
				<div class="form-group">
					<h4 class="mb-3">Теги</h4>
					<div>
						<div 
							v-for="tagGroup in tagGroups" 
							:key="tagGroup.value"
							class="form-group"
						>
							<v-select
								v-model="$v.form.tagsByGroups[tagGroup.value].$model"
								:error-messages="getErrors(`form.tagsByGroups.${tagGroup.value}`)"
								:items="getTagItems(tagGroup.value)"
								:menu-props="{
									maxHeight: 480
								}"
								:label="tagGroup.text"
								:multiple="!((tagGroup.value === 'A7' && form.courseTypeId === $const.eduCourse.type.EDU_MATERIAL) || tagGroup.value === 'A10')"
								persistent-hint
								persistent-placeholder
								:return-object="false"
							>
							</v-select>
						</div>
					</div>
				</div>

				<div class="h-spacer"></div>
				<!-- Form actions -->
				<v-row v-if="allowEdit" no-gutters class="py-3 formActions">
					<v-btn v-if="generator" outlined color="purple darken-3" @click="autofill">
						<v-icon left>mdi-basket-fill</v-icon>
						Заполнить дев-данными
					</v-btn>
					<v-spacer />
					<v-btn 
						dark 
						color="purple darken-3" 
						:loading="loading"
						@click.prevent.stop="save"
					>
						Сохранить
					</v-btn>
				</v-row>
			</div>
		</div>

		<v-snackbar
			v-model="snackbar.success"
			:timeout="2500"
		>
			<div>Элемент успешно сохранен!</div>
			<div class="text-caption">Не забывайте, что выгрузка на сервер выполняется вручную.</div>
			<template v-slot:action="{ attrs }">
				<v-btn
					color="white"
					text
					v-bind="attrs"
					@click="snackbar.success = false"
				>
					Закрыть
				</v-btn>
			</template>
		</v-snackbar>

		<!-- Upload image error dialog -->
        <v-dialog v-model="dialog.uploadImage.show" persistent max-width="480">
            <v-card>
                <v-card-title class="text-h5">
                    Ошибка загрузки изображения
                </v-card-title>
                <v-card-text>
                    При загрузке изображения произошла ошибка. Попробуйте еще раз или обратитесь к разработчику.
				</v-card-text>
                <v-card-actions>
                    <v-btn 
						dark 
						color="purple darken-3" 
						class="mx-auto"
						@click="closeUploadImageDialog"
					>
                        Ок
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- END Upload image error dialog -->

		<!-- Upload data dialog -->
        <v-dialog v-model="dialog.uploadZip.show" persistent max-width="480">
            <v-card>
                <v-card-title class="text-h5">
                    Загрузка данных ЦОК в виде архива
                </v-card-title>
                <v-card-text>
                    <v-file-input
						v-model="dialog.uploadZip.file"
						:key="_.now()"
						accept=".zip"
						label="Выберите архив с данными ЦОК"
						outlined
						dense
						:disabled="dialog.uploadZip.loading"
					></v-file-input>
				</v-card-text>
                <v-card-actions>
					<v-spacer />
					<v-btn 
						dark 
						color="purple darken-3" 
						:loading="dialog.uploadZip.loading"
						@click="uploadZip"
					>
                        Загрузить
                    </v-btn>
                    <v-btn 
						dark 
						color="purple darken-3" 
						:disabled="dialog.uploadZip.loading"
						@click="dialog.uploadZip.show = false"
					>
                        Закрыть
                    </v-btn>
					<v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- END Upload data dialog -->

		<!-- App build laoding dialog -->
        <v-dialog v-model="dialog.appBuild.show" persistent max-width="480">
            <v-card>
                <v-card-title class="text-h5">
                    Сборка приложения
                </v-card-title>
                <v-card-text class="d-flex flex-row flex-nowrap justify-center align-center">
					<v-alert v-if="!_.isNil(dialog.appBuild.error)" type="error">
						{{ dialog.appBuild.error }}
					</v-alert>
					<div v-else>
						<p>Пожалуйста, дождитесь окончания сборки приложения. Время выполнения операции зависит от размера ЦОК, и занимает несколько минут.</p>
						<div class="d-flex flex-row flex-nowrap justify-center align-center">
							<v-progress-circular
								:size="70"
								:width="7"
								color="purple darken-3"
								indeterminate
							/>
						</div>
					</div>
				</v-card-text>
				<v-card-actions v-if="!_.isNil(dialog.appBuild.error)">
                    <v-btn 
						dark 
						color="purple darken-3" 
						@click="dialog.appBuild.show = false"
					>
                        Закрыть
                    </v-btn>
					<v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- END App generation laoding dialog -->

		<!-- Merge update laoding dialog -->
        <v-dialog v-model="dialog.mergeUpdate.show" persistent max-width="766">
            <v-card>
                <v-card-title class="text-h5">
                    Обновление составных частей объединенного ЦОК
                </v-card-title>
                <v-card-text class="d-flex flex-row flex-nowrap justify-center align-center">
					<v-alert v-if="!_.isNil(dialog.mergeUpdate.error)" type="error">
						{{ dialog.mergeUpdate.error }}
					</v-alert>
					<div v-else>
						<p>Пожалуйста, дождитесь окончания обновления. Время выполнения операции зависит от размера и кол-ва составных частей объединенного ЦОК, и занимает до нескольких минут.</p>
						<div class="d-flex flex-row flex-nowrap justify-center align-center">
							<v-progress-circular
								:size="70"
								:width="7"
								color="purple darken-3"
								indeterminate
							/>
						</div>
					</div>
				</v-card-text>
				<v-card-actions v-if="!_.isNil(dialog.mergeUpdate.error)">
                    <v-btn 
						dark 
						color="purple darken-3" 
						@click="dialog.mergeUpdate.show = false"
					>
                        Закрыть
                    </v-btn>
					<v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- END App generation laoding dialog -->

		<!-- Copy version dialog -->
		<v-dialog v-model="dialog.copyVersion.show" persistent max-width="480">
			<v-card>
				<v-card-title class="text-h5">Копирование версии ЦОК</v-card-title>
				<v-card-text>
					<v-alert v-if="!_.isNil(dialog.copyVersion.error)" type="error">
						{{ dialog.copyVersion.error }}
					</v-alert>
					<div v-if="dialog.copyVersion.loading">
						<p>Пожалуйста, дождитесь окончания обновления. Время выполнения операции зависит от размера и кол-ва составных частей объединенного ЦОК, и занимает до нескольких минут.</p>
						<div class="d-flex flex-row flex-nowrap justify-center align-center">
							<v-progress-circular
								:size="70"
								:width="7"
								color="purple darken-3"
								indeterminate
							/>
						</div>
					</div>

					<div>
						<v-select
							v-model="dialog.copyVersion.to_version"
							:items="eduCourseModelVersionListForCopy"
							:menu-props="{
								maxHeight: 480
							}"
							label="Целевой вариант контента"
							persistent-hint
							persistent-placeholder
							:return-object="false"
						>
						</v-select>
					</div>

					<v-radio-group hide-details>
						<template v-slot:label>
							Также скопировать архив с контентом:
						</template>
					</v-radio-group>
					<v-switch
						v-model="dialog.copyVersion.content"
						label="Да"
					></v-switch>
					
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn
						dark
						color="purple darken-3"
						:loading="dialog.copyVersion.loading"
						@click="copyVersion"
					>
						Копировать
					</v-btn>
					<v-btn
						v-if="!dialog.copyVersion.loading"
						dark
						color="purple darken-3"
						@click="dialog.copyVersion.show = false"
					>
						Закрыть
					</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>
        <!-- END Copy version dialog -->
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { vuelidateErrorMixin } from '@/helpers/VuelidateHelpers'
import { validationMixin } from 'vuelidate'
import { required, url } from 'vuelidate/lib/validators'
const DEFAULT_FORM = {
	isNewRecord: false,
	externalId: '',
	externalParentId: null,
	courseTypeId: 0,
	courseImage: null,
	externalLink: '',
	grades: [],
	courseName: '',
	courseDescription: '',
	tags: [],
	tagsByGroups: {
		A1: [],
		A2: [],
		A3: [],
		A4: [],
		A5: [],
		A6: [],
		A7: [],
		A8: [],
		A9: [],
		A10: []
	}
}
export default {
	name: 'DigitalEduCourseForm',
	mixins: [validationMixin, vuelidateErrorMixin],
	props: {
		generator: Boolean,
		model: Object
	},
	data() {
		return {
			dialog: {
				uploadZip: {
					show: false,
					error: null,
					loading: false,
					progress: 0,
					file: null
				},
				uploadImage: {
					show: false,
					error: null
				},
				appBuild: {
					show: false,
					error: null
				},
				mergeUpdate: {
					show: false,
					error: null
				},
				copyVersion: {
					show: false,
					to_version: 'public',
					content: true,
					error: null,
					loading: false,
					progress: 0,
				}
			},
			snackbar: {
				success: false
			},
			form: null,
			loading: false,
			syncLoading: false,
			downloadLoading: false,
			linksLoading: false,
			linksWordLoading: false
		}
	},
	computed: {
		...mapGetters('app', ['eduCourseParentModel', 'eduCourseModelVersionList']),
		...mapGetters("user", ["allowEdit"]),
		isVersionPublic() {
			return _.get(this, 'model.version') === 'public';
		},
		isVersionUnpublished() {
			return _.get(this, 'model.version') === 'unpublished';
		},
		isVersionMinistry() {
			return _.get(this, 'model.version') === 'ministry';
		},
		typeLabel() {
			return _.get(this.$const.eduCourse.types, _.get(this, 'form.courseTypeId', 0))
		},
		imageSrc() {
			return `data:image/png;base64,${_.get(this.form, 'courseImage', '')}`;
		},
		grades() {
			return _.map(Array(16), (v,k) => {
				const n = k + 1;
				return {
					text: `${n <= 11 ? n : n - 11} ${n <= 11 ? 'класс общеобразовательной школы' : 'курс'}`,
					value: n
				}
			})
		},
		isSelectedAllGrades () {
			return this.form.grades.length === this.grades.length
		},
		isSelectedSomeGrades () {
			return this.form.grades.length > 0 && !this.isSelectedAllGrades;
		},
		gradesSelectAllIcon () {
			if (this.isSelectedAllGrades) return 'mdi-close-box'
			if (this.isSelectedSomeGrades) return 'mdi-minus-box'
			return 'mdi-checkbox-blank-outline'
		},
		tagGroups() {
			let result = [];
			_.each(this.$const.eduCourse.tags.groupLabels, (v,k) => {
				result.push({
					text: v,
					value: k
				});
			});
			return result;
		},
		customUrl() {
			return _.join([this.model.url, `rootCustomView=1&rootBackgroundUrl=${btoa(_.get(this, 'form.preview.rootBackgroundUrl', ''))}`], this.form.courseTypeId > 0 ? '&' : '?');
		},
		versionFormatted() {
			return _.get(_.find(this.eduCourseModelVersionList, {value: this.model.version}), 'text', 'Не указан');
		},
		eduCourseModelVersionListForCopy() {
			if(this.model)
				return _.filter(this.eduCourseModelVersionList, o => o.value !== this.model.version);
			return [];
		}
	},
	validations() {
		const requiredForDigitalEduContent = v => {
			return this.form.courseTypeId === this.$const.eduCourse.type.DIGITAL_EDU_CONTENT ? required(v) : true;
		}
		const requiredForDigitalEduContentAndEduMaterial = v => {
			return _.includes([this.$const.eduCourse.type.DIGITAL_EDU_CONTENT, this.$const.eduCourse.type.EDU_MATERIAL], this.form.courseTypeId) ? required(v) : true;
		}

		return {
			form: {
				version: { required },
				externalId: { 
					required,
					format(v) {
						return /^[0-9a-zA-Z-_]*$/gi.test(v);
					}
				},
				courseName: { required },
				courseDescription: {
					required(v) {
						return requiredForDigitalEduContent(v);
					}
				},
				courseImage: {
					required(v) {
						return requiredForDigitalEduContent(v);
					}
				},
				grades: {
					required(v) {
						return requiredForDigitalEduContent(v);
					}
				},
				externalLink: { 
					required(v) {
						return requiredForDigitalEduContent(v);
					},
					...(process.env.NODE_ENV !== 'production' ? {} : {url})
				},
				tagsByGroups: _.mapValues(this.$const.eduCourse.tags.groupLabels, () => {
					return {
						required(v) {
							return requiredForDigitalEduContentAndEduMaterial(v);
						}
					}
				})
			}
		}
	},
	methods: {
		init(obj) {
			let data = _.get(_.cloneDeep(obj), 'data', {});
			for(let f in data)
				data[f] = !_.isNil(this.model) && _.has(this.model, f) ? this.model[f] : data[f];

			this.form = _.assign(
				_.has(obj, 'id') ? {id: obj.id} : {},
				{
					version: _.get(obj, 'version', 'public'),
					enabled: _.get(obj, 'enabled', 1),
					url: _.get(obj, 'url', null),
					shortCustomUrl: _.get(obj, 'shortCustomUrl', null),
					appWindowsUrl: _.get(obj, 'appWindowsUrl', null),
					appLinuxUrl: _.get(obj, 'appLinuxUrl', null),
					preview: _.get(obj, 'preview', {}),
					extra: {
						appIcon: null,
						methodicalManual: null,
						..._.get(obj, 'extra', {})
					}
				},
				_.cloneDeep(DEFAULT_FORM), 
				data,
				{
					sync: _.get(obj, 'sync', false) === true ? 1 : 0
				}
			)
			this.form.tagsByGroups = {
				...DEFAULT_FORM.tagsByGroups,
				...this.form.tagsByGroups
			}
			this.$v.$reset();
		},
		async save() {
			this.loading = true;
			this.serverErrors = null;

			if (this.validate()) {
				const form = {
					..._.pick(this.form, ['id', 'sync', 'version', 'externalId', 'externalParentId', 'courseTypeId', 'courseName', 'enabled']),
					data: {
						..._.omit(this.form, ['isNewRecord']),
						export: this.getExportForm()
					}
				}
				const { success, error, data, syncTotal } = await this.$store.dispatch('app/save', form);

				if (success) {
					this.$store.commit('app/UPDATE_EDU_COURSES', [data]);
					this.$store.commit('app/UPDATE_EDU_COURSE_SYNC_TOTAL', _.isNumber(syncTotal) ? syncTotal : 0);
					this.snackbar.success = true;
					this.$emit('success', data.id);
				} else
					this.serverErrors = error;
			}

			// Scroll to first error if form has any errors
			if(this.hasAnyErrors)
			{
				this.$nextTick(() => {
					const $elFirstError = document.querySelector('.digital-edu-course-form .error--text');
					if(!_.isNil($elFirstError))
						$elFirstError.scrollIntoView(false);
				})
			}

			this.loading = false;
			return;
		},
		async sync() {
			this.syncLoading = true;
			const { success } = await this.$store.dispatch('app/sync', {
				id: _.get(this, 'form.externalId', 0)
			});
			if(success)
				this.model.synced = true;
				
			this.syncLoading = false;
		},
		async uploadImage(e) {
			// Validate type & size
			if(e.type !== 'image/png' || e.size > 512000)
			{
				this.dialog.uploadImage.error = 'Для загрузки разрешены только изображения в формате .PNG размером не более 500Кб';
				this.dialog.uploadImage.show = true;
				return ;
			}

			const reader = new FileReader();
			reader.readAsBinaryString(e);

			reader.onload = event => {
				this.form.courseImage = btoa(event.target.result);
			};

			reader.onerror = error => {
				console.log(['image upload error', error])
				this.dialog.uploadImage = true;
			};
		},
		async uploadPreviewRootBackground(e) {
			const { success, data } = await this.$store.dispatch('app/uploadFile', {
				file: e,
				path: 'images'
			})

			if(success)
			{
				this.form['preview'] = {};
				this.$set(this.form.preview, 'rootBackgroundUrl', data.url);
			}
		},
		async uploadExtraAppIcon(e) {
			const { success, data } = await this.$store.dispatch('app/uploadFile', {
				file: e,
				path: 'images'
			})

			if(success)
			{
				if(!_.has(this, 'form.extra'))
					this.form['extra'] = {};
				this.$set(this.form.extra, 'appIcon', data.url);
			}
		},
		async uploadExtraMethodicalManual(e) {
			const { success, data } = await this.$store.dispatch('app/uploadFile', {
				file: e,
				path: 'docs/methodical-manual'
			})

			if(success)
			{
				if(!_.has(this, 'form.extra'))
					this.form['extra'] = {};
				this.$set(this.form.extra, 'methodicalManual', data.url);
			}
		},
		async downloadZip() {
			this.downloadLoading = true;
			const { success, data } = await this.$store.dispatch('app/downloadData', {
				externalId: this.model.externalId,
				version: this.model.version
			});
			this.downloadLoading = false;
			if(success)
			{
				const link = document.createElement('a');
				// link.target = '_blank';
                link.href = data.url;
                link.download = data.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
			}
		},
		async uploadZip() {
			// Show dialog
			if(this.dialog.uploadZip.show !== true)
			{
				this.dialog.uploadZip.show = true;
				return ;
			}
			// Upload handler
			this.dialog.uploadZip.loading = true;
			this.dialog.uploadZip.progress = 0;

			const { data } = await this.$store.dispatch('app/uploadData', {
				file: this.dialog.uploadZip.file,
				query: {
					externalId: this.model.externalId,
					version: this.model.version
				}
			})

			if(!_.isNil(_.get(data, 'url')))
			{
				this.$store.commit('app/UPDATE_EDU_COURSES', [{
					...this.model,
					url: data.url
				}]);
			}


			this.dialog.uploadZip.show = false;
			this.dialog.uploadZip.loading = false;
			this.dialog.uploadZip.progress = 0;
			this.dialog.uploadZip.file = null;
		},
		async getContentLinks() {
			this.linksLoading = true;
			const { success, data } = await this.$store.dispatch('app/links', {
				externalId: this.model.externalId,
				version: this.model.version
			});
			this.linksLoading = false;

			if(success)
			{
				const csv = [
					['', this.model.courseName, '', this.model.versionUrl].join(';'),
					..._.map(data, o => {
						return [o.id, o.name, o.dir || '', o.url].join(';');
					})
				].join('\r\n');
				const blob = new Blob(["\uFEFF"+csv], { type: 'text/csv;charset=utf-8;' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				// link.target = '_blank';
                link.href = url;
                link.download = 'Ссылки_' + this.model.courseName + '.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
			}
		},
		async getContentLinksWord() {
			this.linksWordLoading = true;
			const { success, data } = await this.$store.dispatch('app/linksWord', {
				externalId: this.model.externalId,
				version: this.model.version
			});
			this.linksWordLoading = false;

			if(success)
			{
				const link = document.createElement('a');
				// link.target = '_blank';
                link.href = data.url;
                link.download = 'Ссылки_' + this.model.courseName + '.docx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
			}
		},
		closeUploadImageDialog() {
			this.dialog.uploadImage.error = null;
			this.dialog.uploadImage.show = false;
		},
		toggleGradesSelectAll() {
			this.$nextTick(() => {
				if (this.isSelectedAllGrades) {
					this.form.grades = [];
				} else {
					this.form.grades = _.map(this.grades, 'value');
				}
			})
		},
		getShortGrade(value) {
			return value.replace('класс общеобразовательной школы', 'кл.');
		},
		getTagItems(group) {
			return _.map(this.$store.getters['app/eduCoursesTagsOfGroup'](group), o => {
				return {
					text: o.name,
					value: o.id
				}
			})
		},
		getExportForm() {
			return _.pick(_.cloneDeep(this.form), [
				'externalId', 
				'courseTypeId', 
				'externalParentId', 
				'courseImage', 
				'externalLink', 
				'grades', 
				'courseName', 
				'courseDescription', 
				'tags'
			])
		},
		autofill() {
			const randString = (length = 32, charset = ['0-9', 'A-Z', 'a-z']) => {
				let result = '';
				const chars = _.join(_.map(charset, v => {
					return {
						'0-9': '0123456789',
						'A-Z': 'ABCDEFGHIJKLMNOPQRSTUVWXTZ',
						'a-z': 'abcdefghijklmnopqrstuvwxtz'
					}[v];
				}), '');
				for(; result.length < length;) result += chars[(Math.random() * chars.length - 1) | 0];
				
				return result;
			}

			let obj = {
				...(_.has(this.model, 'id') ? {id: this.model.id} : {})
			}
			// Text fields
			_.each(['externalId', 'courseDescription'], v => {
				obj[v] = randString(_.random(8, 20));
			})
			// Special fields
			obj['courseName'] = obj.externalId;
			obj['courseImage'] = "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";
			obj['externalLink'] = `https://${randString(_.random(5, 10))}.${randString(_.random(2, 3), ['a-z'])}`;
			obj['grades'] =[_.random(0, 16)];
			obj['tagsByGroups'] = {
				A1: [this.$store.getters['app/eduCoursesTagsOfGroup']('A1')[0].id],
				A2: [this.$store.getters['app/eduCoursesTagsOfGroup']('A2')[0].id],
				A3: [this.$store.getters['app/eduCoursesTagsOfGroup']('A3')[0].id],
				A4: [this.$store.getters['app/eduCoursesTagsOfGroup']('A4')[0].id],
				A5: [this.$store.getters['app/eduCoursesTagsOfGroup']('A5')[0].id],
				A6: [this.$store.getters['app/eduCoursesTagsOfGroup']('A6')[0].id],
				A7: [this.$store.getters['app/eduCoursesTagsOfGroup']('A7')[0].id],
				A8: [this.$store.getters['app/eduCoursesTagsOfGroup']('A8')[0].id],
				A9: [this.$store.getters['app/eduCoursesTagsOfGroup']('A9')[0].id],
				A10: [this.$store.getters['app/eduCoursesTagsOfGroup']('A10')[0].id]
			}

			this.init({
				data: _.assign(this.form, obj)
			});
		},
		async buildApp(os) {
			this.dialog.appBuild.error = null;
			this.dialog.appBuild.show = true;
			
			const { success, data, error } = await this.$store.dispatch('app/buildApp', {
				externalId: this.form.externalId,
				os: os
			});
			
			if(success)
				this.$store.commit('app/UPDATE_EDU_COURSES', [data]);
			else
				this.dialog.appBuild.error = error;

			this.dialog.appBuild.show = false;
		},
		async mergeUpdate() {
			// Show dialog
			this.dialog.mergeUpdate.error = null;
			this.dialog.mergeUpdate.show = true;

			const { success, error } = await this.$store.dispatch('app/mergeUpdate', {
				externalId: this.form.externalId,
				version: this.form.version
			})

			if(!success)
			{
				this.dialog.mergeUpdate.error = error;
				return ;
			}
			else
				this.dialog.mergeUpdate.show = false;
		},
		async copyVersion() {
			// Show 
			if(this.dialog.copyVersion.show !== true)
			{
				this.dialog.copyVersion.to_version = _.get(this, 'eduCourseModelVersionListForCopy.0.value', null);
				this.dialog.copyVersion.show = true;
				return ;
			}
			// Copy
			this.dialog.copyVersion.loading = true;
            const { success, error } = await this.$store.dispatch(
                "app/copyVersion",
                {
					externalId: this.model.externalId,
					version: this.model.version,
                    ..._.pick(this.dialog.copyVersion, [
                        "to_version",
                        "content"
                    ])
                }
            );
			this.dialog.copyVersion.loading = false;
            if (!success) this.dialog.copyVersion.error = error;
            else {
                this.$emit('fetch', this.model.id);
                this.dialog.copyVersion.show = false;
            }
		}
	},
	created() {
		this.init(this.model);
	},
	watch: {
		model(val) {
			this.init(val);
		},
		['form.tagsByGroups']: {
			deep: true,
			handler(val) {
				this.form.tags = _.map(_.flatMap(val, o => _.isObject(o) ? _.toArray(o) : o), v => {
					return {id: v};
				});
			}
		},
		['form.externalId']: {
			handler(val) {
				this.form.externalLink = _.size(val) > 0 ? _.join([window.location.origin, 'dec', val], '/') : '';
			}
		}
	}
} 
</script>

<style scoped lang="scss">
.form-group{
	$my: 16px;
	margin-top: $my;
	margin-bottom: $my;
}
.h-spacer {
	height: 50px;
}
.formActions {
	position: fixed;
	bottom: 0;
	width: 56%;
	background-color: white;
	border-top: solid 1px #949494;
	padding-right: 20px;
}
</style>
